import { ServiceScraperResponse } from '../../types/serviceScraperResponseTypes';
import {
    ScraperAttribute,
    ScraperAttributeStatus,
    ScraperCategory,
    ScraperService,
    ScraperServiceStatus,
} from '../../types/scrapedServicesTypes';
import {
    ComplementaryServiceAttributeState,
    ComplementaryServiceTypeState,
    ComplementaryServiceCategoryState,
    ZinantisServiceState,
} from '../../types/zitoneoServicesTypes';
import { getRandomNumber } from '../../../../utils/usefulFunctions';
import {
    inferScraperAttributeFieldType,
    normalizeScraperAttributeValue,
} from '../../utils/AIScraperPanelsUtils';

export const adaptScrapedServicesToState = (
    data: ServiceScraperResponse,
    serviceTypes: ComplementaryServiceTypeState[],
    serviceCategories: ComplementaryServiceCategoryState[],
    serviceAttributes: ComplementaryServiceAttributeState[],
    hotelServices: ZinantisServiceState[]
): ScraperCategory[] => {
    return data.Results.map((result): ScraperCategory => {
        const serviceCategory = serviceCategories.find(
            category => parseInt(category.Id) === result.IdServiceCategory
        );

        return {
            IdServiceCategory: result.IdServiceCategory,
            ServiceCategory: serviceCategory ? serviceCategory.Name : '',
            Icon: serviceCategory ? serviceCategory.Icon : '',
            Services: result.ScrapingServiceResponse.KnownServices.flatMap(
                (service): ScraperService[] => {
                    const serviceType = serviceTypes.find(
                        type => parseInt(type.Id, 10) === service.IdServiceType
                    );
                    if (!serviceType) {
                        return [];
                    }

                    const existingService = hotelServices.find(
                        hs => hs.IdServiceType === service.IdServiceType
                    );
                    return [
                        {
                            Id: existingService
                                ? existingService.Id
                                : `-${getRandomNumber()}`,
                            IdServiceType: service.IdServiceType,
                            ServiceType: serviceType.Name,
                            Checked: true,
                            Status: existingService
                                ? ScraperServiceStatus.EXISTING
                                : ScraperServiceStatus.NEW,
                            AttributeDefinitions:
                                service.AttributeDefinitionValues.map(
                                    attribute => {
                                        const serviceAttribute =
                                            serviceAttributes.find(
                                                attr =>
                                                    parseInt(attr.Id) ===
                                                    attribute.IdAttributeDefinition
                                            );
                                        const existingAttribute =
                                            existingService?.AttributeValues?.find(
                                                attr =>
                                                    !attr.Deleted &&
                                                    attr.IdAttributeDefinition ===
                                                        attribute.IdAttributeDefinition
                                            );
                                        const currentValue =
                                            existingAttribute?.Value ?? null;
                                        const fieldType =
                                            inferScraperAttributeFieldType(
                                                attribute.Value?.trim()
                                                    ? attribute.Value
                                                    : currentValue
                                            );
                                        const suggestedValue =
                                            normalizeScraperAttributeValue(
                                                attribute.Value,
                                                fieldType
                                            );
                                        const normalizedCurrentValue =
                                            normalizeScraperAttributeValue(
                                                currentValue,
                                                fieldType
                                            );

                                        const status = normalizedCurrentValue
                                            ? normalizedCurrentValue ===
                                              suggestedValue
                                                ? ScraperAttributeStatus.MATCHED
                                                : ScraperAttributeStatus.CONFLICT
                                            : ScraperAttributeStatus.NEW;

                                        return {
                                            Id:
                                                existingAttribute?.Id ??
                                                `-${getRandomNumber()}`,
                                            IdAttributeDefinition:
                                                attribute.IdAttributeDefinition,
                                            AttributeDefinition:
                                                serviceAttribute
                                                    ? serviceAttribute.Name
                                                    : '',
                                            Checked: true,
                                            Value: suggestedValue,
                                            CurrentValue: currentValue,
                                            SuggestedValue: suggestedValue,
                                            SelectedValueSource:
                                                'SuggestedValue',
                                            Status: status,
                                            FieldType: fieldType,
                                        };
                                    }
                                ),
                        },
                    ];
                }
            ),
        };
    });
};

/**
 * Resolves the value to persist for a scraped attribute. Conflicts persist the
 * value chosen by the user (current or suggested); other attributes persist
 * their edited value.
 * @param attribute Scraped attribute.
 * @returns The value to store in the database.
 */
const getScraperAttributeValueToSave = (
    attribute: ScraperAttribute
): string => {
    let value = attribute.Value;
    if (attribute.Status === ScraperAttributeStatus.CONFLICT) {
        value =
            attribute.SelectedValueSource === 'CurrentValue'
                ? attribute.CurrentValue ?? ''
                : attribute.SuggestedValue;
    }
    return value;
};

export const adaptScrapedServicesStateToDDBB = (
    scrapedData: ScraperCategory[],
    idHotel: number,
    idSysUser: number
) => {
    const services = scrapedData.flatMap(category => category.Services);
    return {
        Id: idHotel.toString(),
        HotelZitoneoChange: {
            Id: `-${getRandomNumber()}`,
            ChangeDate: new Date(),
            IdSystemUser: idSysUser,
            IdHotel: idHotel,
            __isAdded: true,
            __isChanged: true,
        },
        HotelServiceTypes: services
            .filter(service => service.Checked)
            .map(service => {
                return {
                    Id: service.Id,
                    Enabled: true,
                    IdServiceType: service.IdServiceType,
                    AttributeDefinitionValues:
                        service.AttributeDefinitions.filter(
                            attr => attr.Checked
                        ).map(attr => {
                            return {
                                Id: attr.Id,
                                IdAttributeDefinition:
                                    attr.IdAttributeDefinition,
                                Value: getScraperAttributeValueToSave(attr),
                                __isAdded: parseInt(attr.Id, 10) < 0,
                                __isChanged: true,
                            };
                        }),
                    __isAdded: service.Status === ScraperServiceStatus.NEW,
                    __isChanged: true,
                };
            }),
    };
};
